import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionHeader } from "../components/Services";
import { INDUSTRIES } from "../components/Industries";
import FinalCTA from "../components/FinalCTA";

const DEEP = {
    Finance: {
        kpi: "Avg. response rate · 47%",
        bullets: [
            "Banking experience & onboarding studies",
            "Investor sentiment pulses",
            "Payments and digital wallet research",
        ],
    },
    Healthcare: {
        kpi: "Validated rows · 99.2%",
        bullets: [
            "Patient adherence & access studies",
            "HCP and KOL interviews",
            "Therapy area perceptions",
        ],
    },
    Telecom: {
        kpi: "Network NPS programs · 12 markets",
        bullets: [
            "Churn driver studies",
            "Plan & pricing optimisation",
            "Service quality tracking",
        ],
    },
    "E-commerce": {
        kpi: "Avg. survey length · 7.8 min",
        bullets: [
            "Shopper journey mapping",
            "Post-purchase satisfaction",
            "Returns and reverse-logistics",
        ],
    },
    Government: {
        kpi: "Citizen samples · 50k+",
        bullets: [
            "Public service experience",
            "Awareness and access surveys",
            "Program evaluation & impact studies",
        ],
    },
    Startups: {
        kpi: "Time to insight · 7 days",
        bullets: [
            "Pricing tests & ICP discovery",
            "Pulse studies for PMF",
            "Concept tests & feature prioritisation",
        ],
    },
};

export default function IndustriesPage() {
    return (
        <main className="pt-32" data-testid="industries-page">
            <section className="relative pb-10">
                <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
                <div className="pr-container relative">
                    <SectionHeader
                        overline="Industries"
                        title="Tuned playbooks for regulated and fast-moving sectors"
                        subtitle="Six core verticals, fielded across 12+ countries — each with its own QC and compliance recipe."
                    />
                </div>
            </section>

            <section className="py-12">
                <div className="pr-container grid grid-cols-1 md:grid-cols-2 gap-5">
                    {INDUSTRIES.map((ind, i) => {
                        const Icon = ind.icon;
                        const detail = DEEP[ind.name];
                        return (
                            <motion.div
                                key={ind.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="pr-card rounded-2xl p-7 relative overflow-hidden"
                                data-testid={`industry-detail-${i}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-400/30 flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-blue-300" />
                                        </div>
                                        <div>
                                            <h3 className="font-outfit text-lg font-semibold text-white">
                                                {ind.name}
                                            </h3>
                                            <p className="text-xs font-mono-pr text-emerald-300">
                                                {detail.kpi}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-5 text-sm text-slate-400 leading-relaxed">
                                    {ind.desc}
                                </p>
                                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                                    {detail.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-center gap-2"
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="pr-container pt-10">
                    <Link
                        to="/contact"
                        className="btn-primary"
                        data-testid="industries-page-cta"
                    >
                        Tell us about your sector
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <FinalCTA />
        </main>
    );
}
